// Chunked upload protocol shapes (plan.md §6.2).
//
// Three-step state machine:
//   1. POST /me/uploads/init      — declare metadata + total size →
//                                   server returns either a dedup
//                                   short-circuit (existing fileId)
//                                   OR an upload session token + the
//                                   chunk_size to use.
//   2. PUT  /me/uploads/:uploadId/chunks/:n
//                                  — append chunk bytes; idempotent
//                                   on the chunk index. Returns the
//                                   updated session state (received
//                                   chunks + next expected).
//   3. POST /me/uploads/:uploadId/complete
//                                  — finalise. Server verifies MD5
//                                   matches expected (if supplied),
//                                   commits the file/folder rows,
//                                   and returns the resulting File.
//
// Resume contract: a /me/uploads/:uploadId GET returns the session
// state including `receivedChunks[]` so a re-attached SPA knows which
// chunks to skip. Idempotency on PUT means a re-PUT for an already-
// received chunk is a 200 no-op.
import { z } from 'zod';
import { File } from './file.js';
import { FilePartition } from './folder.js';
import { FileGroupScope } from './scope.js';
/** Strategy on a same-name conflict at upload-complete time. */
export const SameNameStrategy = z.enum(['overwrite', 'keep-history', 'rename', 'reject']);
export const UploadState = z.enum(['in-progress', 'completed', 'aborted']);
export const UploadSession = z.object({
    id: z.string().uuid(),
    ownerId: z.string().uuid(),
    targetFolderId: z.string().uuid().nullable(),
    partition: FilePartition,
    filename: z.string().min(1).max(255),
    mimeType: z.string().min(1).max(255),
    totalSizeBytes: z.number().int().nonnegative(),
    expectedMd5: z.string().regex(/^[0-9a-f]{32}$/).nullable(),
    chunkSize: z.number().int().min(1024).max(67_108_864),
    /** Sorted-distinct array of received chunk indices. */
    receivedChunks: z.array(z.number().int().nonnegative()),
    groupScopes: z.array(FileGroupScope),
    receivedBytes: z.number().int().nonnegative(),
    state: UploadState,
    sameNameStrategy: SameNameStrategy,
    /** When init resolved as a dedup match, points at the existing file. */
    dedupTargetFileId: z.string().uuid().nullable(),
    expiresAt: z.string().datetime({ offset: true }),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
/** Init response — either a dedup short-circuit (kind='deduped') or
 *  the start of a chunked upload (kind='session'). The SPA branches
 *  on `kind`; the discriminated union lets TS narrow without
 *  optional-field gymnastics. */
export const UploadInitResponse = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('deduped'),
        /** The existing file the new upload was deduped against. The SPA
         *  can either link the existing file in the desired folder OR
         *  do nothing (it is already in your library). */
        existingFile: File,
    }),
    z.object({
        kind: z.literal('session'),
        session: UploadSession,
    }),
]);
/** Sweeper window — sessions older than this without progress are
 *  considered abandoned and purged. */
export const UPLOAD_SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 24 h
//# sourceMappingURL=upload.js.map